import { useAtom } from '@reatom/npm-react'
import { formatDistanceStrict } from 'date-fns'
import styled from 'styled-components'

import { AsyncStateN, pipe } from '@blade/std-lib'
import { Skeleton } from '@blade/ui-kit'
import {
  basicPositionsStateAtom,
  formatPercent,
  formatQuantity,
  formatSignedUsd,
  formatUsd,
} from '@root/entities/basic'
import { useTranslation } from '@root/shared/localization'

import type { BasicPosition } from '@root/entities/basic'

/**
 * What the balance is holding.
 *
 * Every figure is a valuation at a price a job fetched, not a quote from this
 * second, so the list says when the prices are from. A total with no date
 * reads as live, and a live-looking number an hour old is how somebody
 * decides to sell on a price that has already gone.
 */

/** Past this the date is a warning: the job runs every minute. */
const STALE_AFTER_MS = 15 * 60 * 1000

export const BasicPositions: React.FC = () => {
  const { t } = useTranslation()
  const [state] = useAtom(basicPositionsStateAtom)

  const data = pipe(
    state,
    AsyncStateN.getOrElseW(() => undefined),
  )

  if (!data) {
    return (
      <Root>
        <Skeleton height={64} width="100%" />
        <Skeleton height={64} width="100%" />
      </Root>
    )
  }

  if (data.positions.length === 0) {
    return (
      <Root>
        <Empty>{t('basic.no_positions')}</Empty>
      </Root>
    )
  }

  const now = new Date()
  const asOf = data.totals.pricesAsOf
    ? new Date(data.totals.pricesAsOf)
    : undefined
  const stale = asOf ? now.getTime() - asOf.getTime() > STALE_AFTER_MS : false

  return (
    <Root>
      <Totals>
        <TotalsValue>{formatUsd(data.totals.valueUsd)}</TotalsValue>
        <TotalsPnl $down={(data.totals.pnlUsd ?? 0) < 0}>
          {formatSignedUsd(data.totals.pnlUsd ?? 0)}
          {data.totals.pnlRatio === undefined
            ? ''
            : ` (${formatPercent(data.totals.pnlRatio)})`}
        </TotalsPnl>
      </Totals>

      {data.positions.map((position) => (
        <PositionRow key={position.id} position={position} />
      ))}

      <AsOf $stale={stale}>
        {asOf
          ? t(stale ? 'basic.prices_stale' : 'basic.prices_as_of', {
              when: formatDistanceStrict(asOf, now, { addSuffix: true }),
            })
          : t('basic.no_prices')}
      </AsOf>
    </Root>
  )
}

const PositionRow: React.FC<{ position: BasicPosition }> = ({ position }) => {
  const { t } = useTranslation()
  // A position nobody has priced is shown with what it cost and no value:
  // filling the gap with the cost would read as a token that has not moved.
  const priced = position.valueUsd !== undefined

  return (
    <Row>
      <Held>
        <Symbol>{position.symbol}</Symbol>
        <Quantity>{formatQuantity(position.quantity)}</Quantity>
      </Held>
      <Values>
        <Value>
          {priced
            ? formatUsd(position.valueUsd!)
            : t('basic.unvalued', { cost: formatUsd(position.costUsd) })}
        </Value>
        {priced && position.pnlUsd !== undefined ? (
          <Pnl $down={position.pnlUsd < 0}>
            {formatSignedUsd(position.pnlUsd)}
            {position.pnlRatio === undefined
              ? ''
              : ` (${formatPercent(position.pnlRatio)})`}
          </Pnl>
        ) : undefined}
      </Values>
    </Row>
  )
}

const Root = styled.div`
  display: grid;
  gap: 8px;
`

const Totals = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: baseline;
  gap: 8px;
  padding: 12px;

  background: var(--background-3);
  border-radius: 12px;
`

const TotalsValue = styled.span`
  color: var(--text-12);
  font-size: 18px;
  font-weight: 700;
`

const TotalsPnl = styled.span<{ $down: boolean }>`
  color: ${({ $down }) => ($down ? 'var(--error-9)' : 'var(--success-9)')};
  font-size: 13px;
  font-weight: 600;
`

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;

  background: var(--background-2);
  border-radius: 12px;
`

const Held = styled.div`
  display: grid;
  gap: 2px;
`

const Symbol = styled.span`
  color: var(--text-12);
  font-size: 13px;
  font-weight: 700;
`

const Quantity = styled.span`
  color: var(--text-11);
  font-size: 11px;
  font-weight: 500;
`

const Values = styled.div`
  display: grid;
  gap: 2px;
  justify-items: end;
`

const Value = styled.span`
  color: var(--text-12);
  font-size: 13px;
  font-weight: 600;
`

const Pnl = styled.span<{ $down: boolean }>`
  color: ${({ $down }) => ($down ? 'var(--error-9)' : 'var(--success-9)')};
  font-size: 11px;
  font-weight: 600;
`

const Empty = styled.span`
  color: var(--text-11);
  font-size: 12px;
  font-weight: 500;
  text-align: center;
  padding: 16px;
`

const AsOf = styled.span<{ $stale: boolean }>`
  color: ${({ $stale }) =>
    $stale ? 'var(--warning-9, var(--text-11))' : 'var(--text-11)'};
  font-size: 11px;
  font-weight: 500;
`
